import { DataType } from './data';
import type { DeltaOf } from '../utils/delta';
import type {
    ICTBlipFillProperties,
    ICTConnectionDmlA,
    ICTGraphicalObject,
    ICTGroupShapeProperties,
    ICTShapeProperties,
    ICTShapeStyle,
    ICTTextBody,
    ICTTransform2D,
    STBlackWhiteMode,
    ICTGroupShapeMsDsp,
    ICTDataModelDelta,
    ICTDiagramDefinitionDelta,
    ICTStyleDefinitionDelta,
    ICTColorTransformDelta,
    ICTMedia,
} from '@tencent/slide-sdk-pb-model';
import type { IChartSpaceData } from './chart/chart-data';
import type { IApplicationNonVisualPropsData, IBaseShapeData, IDrawingNonVisualPropsData } from './base-shape-data';

/**
 * 类形状的数据
 */
export type IDrawingShapeData =
    | IShapeData
    | IGroupShapeData
    | IConnectorData
    | IPictureData
    | ITableData
    | IChartData
    | ISmartArtData;

export type IPlaceholderShapeData = IShapeData | ITableData | IChartData | IPictureData | ISmartArtData;

/**
 * 普通形状
 */
export interface IShapeData extends IBaseShapeData<DataType.Shape> {
    /**
     * 形状不可见属性
     */
    nvPr: IShapeNonVisualPropsData;
    /**
     * 形状绘图属性
     */
    spPr: ICTShapeProperties;
    /**
     * 主题快速样式
     */
    style?: ICTShapeStyle;
    /**
     * 文本内容
     */
    txBody?: ICTTextBody;
    /**
     * 文本变换
     */
    txXfrm?: ICTTransform2D;
    /**
     * 是否继承幻灯片背景填充
     */
    useBgFill?: boolean;

    /**
     * SmartArt 内图形对应排版节点的 ID（参见 `msdsp2008:CT_ShapeMsDsp`）
     */
    modelId?: string;
}

/**
 * 组合
 */
export interface IGroupShapeData extends IBaseShapeData<DataType.GroupShape> {
    /**
     * 组合不可见属性
     */
    nvPr: IGroupShapeNonVisualPropsData;
    /**
     * 形状绘图属性
     */
    spPr: ICTGroupShapeProperties;
    /**
     * 组合包含的形状列表
     */
    shapeList: IDrawingShapeData[];
}

/**
 * 绘图对象容器（表格、图表、SmartArt）
 */
export interface IGraphicFrameBaseData
    extends IBaseShapeData<
        | DataType.GraphicalObjectFrameTable
        | DataType.GraphicalObjectFrameChart
        | DataType.GraphicalObjectFrameSmartArt
        | DataType.GraphicalObjectFrameUnknown
    > {
    /**
     * 绘图对象不可见属性
     */
    nvPr: IGraphicalObjectFrameNonVisualPropsData;

    /**
     * 形状属性
     */
    spPr: {
        /**
         * 位置和大小
         */
        xfrm?: ICTTransform2D;
    };

    /**
     * 绘图对象
     */
    graphic?: ICTGraphicalObject;

    bwMode?: STBlackWhiteMode;
}

/**
 * 表格
 */
export interface ITableData extends IGraphicFrameBaseData {
    /**
     * 元素类型，包括：形状、组合、连接线、图片和自定义图形
     */
    type: DataType.GraphicalObjectFrameTable;
}

/**
 * 图表
 */
export interface IChartData extends IGraphicFrameBaseData {
    type: DataType.GraphicalObjectFrameChart;
    /**
     * 这里用于前端获取chartSpace时简化层级，是props上的一个get属性
     * 实际的数据在graphic.graphicData.chart上
     */
    chartSpace?: IChartSpaceData;
}

/**
 * SmartArt 数据
 */
export interface ISmartArtData extends IGraphicFrameBaseData {
    type: DataType.GraphicalObjectFrameSmartArt;
    diagram?: IDiagramsGroupData;
}
/**
 * SmartArt 等比例缩放时使用
 */
export interface ScaleTransformParams {
    transform?: NonNullable<DeltaOf<ICTTransform2D>>;
    scaleX?: number;
    scaleY?: number;
}

export interface IDiagramsGroupData {
    /**
     * SmartArt 数据集合
     * */
    dataModel?: ICTDataModelDelta;
    /**
     * drawing 是 shape 的集合
     */
    drawing?: IDiagramDrawing;
    layoutDef?: ICTDiagramDefinitionDelta;
    styleDef?: ICTStyleDefinitionDelta;
    colorDef?: ICTColorTransformDelta;
}

export interface IDiagramDrawing {
    diagramId?: string;
    spTree?: ICTGroupShapeMsDsp;
}

/**
 * 连接线
 */
export interface IConnectorData extends IBaseShapeData<DataType.Connector> {
    /**
     * 连接线不可见属性
     */
    nvPr: IConnectorNonVisualPropsData;
    /**
     * 形状绘图属性
     */
    spPr: ICTShapeProperties;
    /**
     * 主题快速样式
     */
    style?: ICTShapeStyle;
}

/**
 * 图片
 */
export interface IPictureData extends IBaseShapeData<DataType.Picture> {
    /**
     * 图片不可见属性
     */
    nvPr: IPictureNonVisualPropsData;
    /**
     * 形状绘图属性
     */
    spPr: ICTShapeProperties;
    /**
     * 主题快速样式
     */
    style?: ICTShapeStyle;
    /**
     * 图片内容
     */
    blipFill?: ICTBlipFillProperties;

    /**
     * 音视频，给前端提供的接口需要
     */
    video?: IMediaData;
    audio?: IMediaData;

    imageType?: string;
}

// NonVisualProps
export interface IShapeNonVisualPropsData extends IDrawingNonVisualPropsData, IApplicationNonVisualPropsData {
    /**
     * 形状锁
     */
    locking?: IShapeLockingData;
    /**
     * 是否有文本框
     */
    txBox?: boolean;
    /**
     * 是否是公式形状，公式形状可以识别为公式
     */
    isEquationShape?: boolean;
}

export interface IGroupShapeNonVisualPropsData
    extends IDrawingNonVisualPropsData,
        Omit<IApplicationNonVisualPropsData, 'ph'> {
    /**
     * 组合锁
     */
    locking?: IGroupShapeLockingData;
}

export interface IGraphicalObjectFrameNonVisualPropsData
    extends IDrawingNonVisualPropsData,
        IApplicationNonVisualPropsData {
    /**
     * 绘图对象锁
     */
    locking?: IGraphicalObjectFrameLockingData;
}

export interface IConnectorNonVisualPropsData extends IDrawingNonVisualPropsData, IApplicationNonVisualPropsData {
    /**
     * 连接线锁
     */
    locking?: IConnectorLockingData;
    /**
     * 连接线起点
     */
    stCxn?: ICTConnectionDmlA;
    /**
     * 连接线终点
     */
    endCxn?: ICTConnectionDmlA;
}

export interface IPictureNonVisualPropsData extends IDrawingNonVisualPropsData, IApplicationNonVisualPropsData {
    /**
     * 图片锁
     */
    locking?: IPictureLockingData;
    /**
     * 是否相对图片尺寸进行比例缩放
     */
    preferRelativeResize?: boolean;
    /**
     * 媒体参数
     */
    media?: ICTMedia;
}

export interface IMediaData {
    link?: string;
    format?: string;
    contentType?: string;
    size?: number;
    isExternal?: boolean;
}

// Locking
export interface ICommonLockingData {
    noGrp?: boolean;
    noMove?: boolean;
    noChangeAspect?: boolean;
    noResize?: boolean;
    noSelect?: boolean;
}

export interface IShapeLockingData extends ICommonLockingData {
    noRot?: boolean;
    noEditPoints?: boolean;
    noAdjustHandles?: boolean;
    noChangeArrowheads?: boolean;
    noChangeShapeType?: boolean;
    noTextEdit?: boolean;
}

export interface IGroupShapeLockingData extends ICommonLockingData {
    noUngrp?: boolean;
    noRot?: boolean;
}

export interface IGraphicalObjectFrameLockingData extends ICommonLockingData {
    noDrilldown?: boolean;
}

export interface IConnectorLockingData extends ICommonLockingData {
    noRot?: boolean;
    noEditPoints?: boolean;
    noAdjustHandles?: boolean;
    noChangeArrowheads?: boolean;
    noChangeShapeType?: boolean;
}

export interface IPictureLockingData extends ICommonLockingData {
    noRot?: boolean;
    noEditPoints?: boolean;
    noAdjustHandles?: boolean;
    noChangeArrowheads?: boolean;
    noChangeShapeType?: boolean;
    noCrop?: boolean;
}

/**
 * 对带组合图形进行深度遍历
 * @param shape 需要遍历的图形
 * @param callback 遍历到每一个叶子图形和 group 节点时执行回调
 */
export function forEachDescendantShapes(
    groupShape: { id: string; shapeList: IDrawingShapeData[] },
    callback: (
        child: IDrawingShapeData,
        offset: number,
        parent: { id: string; shapeList: IDrawingShapeData[] },
        parentOffset: number,
    ) => void,
    startOffset = 0,
): void {
    for (const [offset, shape] of groupShape.shapeList.entries()) {
        if (shape.type === DataType.GroupShape) {
            forEachDescendantShapes(shape, callback, startOffset + offset);
        }

        callback(shape, offset, groupShape, startOffset);
    }
}

/**
 * 展开带组合图形
 */
export function flattenShapes(shapes: IDrawingShapeData[]) {
    const flattenShapes: IDrawingShapeData[] = [];
    forEachDescendantShapes({ shapeList: shapes, id: '' }, (shape) => flattenShapes.push(shape));
    return flattenShapes;
}
